import { UIElement } from '../ui-element';
import { browser } from '../../global/browser';
import { show } from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import styles from './ui-videobackground.css';

/**
 * TODO GUI-4137 remove this solution
 * @deprecated
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIVideoBackground
 * @element ui-videobackground
 * @classdesc Represents a class for <code>ui-videobackground</code> element.
 * Plays video on background with leaves.
 * @property {string} [color] {@attr color} Color of background color of video tag.
 * @property {string} [poster] {@attr poster} Poster for video tag.
 * @property {boolean} [disabled] {@attr disabled} Is video disabled for render and play.
 * @slot
 * @example
 * <ui-videobackground
 *  poster="/video/background-hd.jpg"
 *  color="#51971B">
 *   <video autoplay muted loop poster="/video/background-hd.jpg">
 *     <source type="video/mp4" src="/video/background-hd.mp4">
 *   </video>
 * </ui-videobackground>
 */
@Component({ tag: 'ui-videobackground', styles })
export class UIVideoBackground extends UIElement {
    static get observedAttributes(): ObservedAttributes {
        return ['poster', 'color'];
    }

    @Attribute(String) color?: string;
    @Attribute(String) poster?: string;
    @Attribute(Boolean) disabled?: boolean;

    /**
     * Do not render video if:
     *   1. has 'disabled' attribute;
     *   2. it is mobile by user agent;
     *   3. matches screen media;
     *   4. device has slow or cellular connection.
     * @returns {boolean}
     */
    isVideoEnabled(): boolean {
        return !(
            this.disabled ||
            browser.isMobile() ||
            browser.mobileMediaMatches() ||
            browser.isSlowOrCellularConnection()
        );
    }

    /**
     * Hash video element.
     * @returns {boolean}
     */
    hasVideo(): boolean {
        return !!this.getVideo();
    }

    /**
     * Get video or create if this isn't has created video.
     * @returns {HTMLVideoElement}
     */
    getVideo(): HTMLVideoElement | null {
        return this.querySelector('video');
    }

    /**
     * Updates poster.
     */
    updatePoster(): void {
        this.style.backgroundImage = this.poster
            ? 'url(' + this.poster + ')'
            : '';
        if (this.hasVideo()) {
            this.getVideo()!.poster = this.poster ? this.poster : '';
        }
    }

    /**
     * Updates color.
     */
    updateColor(): void {
        this.style.backgroundColor = this.color || 'initial';
    }

    /**
     * Runs video after users' interaction.
     */
    runVideoAfterInteraction(): void {
        const video = this.getVideo()!;

        if (!video.paused) {
            return;
        }

        const handleEvents = ['scroll', 'mouseover', 'keydown'];
        const complete = () => {
            handleEvents.forEach((eventName) => {
                document.removeEventListener(eventName, handler);
            });
        };

        const handler = () => {
            if (!video.paused) {
                complete();
            }
            video
                .play()
                .then(complete)
                .catch(() => {}); // ignored
        };

        handleEvents.forEach((eventName) => {
            document.addEventListener(eventName, handler);
        });
    }

    /**
     * Plays the video.
     */
    runVideo(): void {
        const video = this.getVideo();
        if (!video) {
            return;
        }
        show(video);
        if (video.paused) {
            const prom = video.play().catch(() => {}); // ignored
            if (!prom) {
                return;
            }
            prom.catch((err) => {
                console.log(err, err.message);
                this.runVideoAfterInteraction();
            });
        }
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: string | null
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'poster':
                this.updatePoster();
                break;
            case 'color':
                this.updateColor();
                break;
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.setAttribute('aria-hidden', 'true');
        this.updatePoster();
        this.updateColor();
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        if (this.isVideoEnabled()) {
            const picture = this.querySelector('picture');
            this.runVideo();
            if (picture) {
                this.removeChild(picture);
            }
        } else if (this.hasVideo()) {
            let video = this.querySelector('video');
            if (video) {
                video.src = '';
                this.removeChild(video);
                video = null;
            }
        }
    }
}
